import { DynamicConfigName, ExperimentName, GateName, LayerName } from 'shared/types/experiments'
import { StatsigClient, StatsigUser } from '@statsig/js-client'
import { createAtom, runInAction } from 'mobx'

type FeatureGate = ReturnType<StatsigClient['getFeatureGate']>
type Experiment = ReturnType<StatsigClient['getExperiment']>
type Layer = ReturnType<StatsigClient['getLayer']>
type DynamicConfig = ReturnType<StatsigClient['getDynamicConfig']>

export type GateState = 'loading' | 'on' | 'off'

// Statsig's own network timeout (`DEFAULT_TIMEOUT_MS` in client-core). A failed fetch can
// leave the client in Loading with no event, so a client that is not Ready by then counts
// as unable to evaluate and every gate state is `off`.
const GATE_SETTLE_TIMEOUT_MS = 10_000

const emptyFeatureGate: FeatureGate = {
  name: '',
  value: false,
  ruleID: '',
  details: null,
  idType: '',
  __evaluation: null,
}

const emptyExperiment: Experiment = {
  name: '',
  ruleID: '',
  value: {},
  details: null,
  groupName: null,
  __evaluation: null,
  get: (key, fallback) => fallback as any,
}

const emptyLayer: Layer = {
  name: '',
  ruleID: '',
  details: null,
  groupName: null,
  __value: {},
  __evaluation: null,
  get: (key, fallback) => fallback as any,
}

const emptyDynamicConfig: DynamicConfig = {
  name: '',
  ruleID: '',
  value: {},
  details: null,
  __evaluation: null,
  get: (key, fallback) => fallback as any,
}

export class ExperimentsStore {
  storeName = 'ExperimentsStore'
  private client: StatsigClient
  private isActive = false
  private valuesAtom = createAtom('ExperimentsStore.values')
  private gatesStatus: 'loading' | 'ready' | 'failed' = 'loading'
  private settleTimeout: ReturnType<typeof setTimeout> | undefined

  setClient(client: StatsigClient) {
    if (this.client === client) {
      return
    }
    this.client?.off('values_updated', this.handleValuesUpdated)
    clearTimeout(this.settleTimeout)

    this.client = client
    this.isActive = true
    this.gatesStatus = client.loadingStatus === 'Ready' ? 'ready' : 'loading'
    client.on('values_updated', this.handleValuesUpdated)
    if (this.gatesStatus === 'loading') {
      this.settleTimeout = setTimeout(() => this.settle('failed'), GATE_SETTLE_TIMEOUT_MS)
    }
  }

  private handleValuesUpdated = () => {
    if (this.client.loadingStatus === 'Ready') {
      this.settle('ready')
    }
  }

  // Once Ready, gates keep answering on/off, also while `identify()` refetches.
  private settle(status: 'ready' | 'failed') {
    clearTimeout(this.settleTimeout)
    this.gatesStatus = status
    runInAction(() => this.valuesAtom.reportChanged())
  }

  /**
   * Whether the Statsig client has arrived. `logEvent` drops silently until it has
   * — no throw, no warning, no queue — so a caller that must not lose an event has
   * to ask before firing.
   */
  get isReady(): boolean {
    return this.isActive
  }

  getFeatureGate(name: GateName): FeatureGate {
    if (!this.isActive) {
      return emptyFeatureGate
    }
    return this.client.getFeatureGate(name)
  }

  getGateState(name: GateName): GateState {
    this.valuesAtom.reportObserved()
    if (!this.isActive || this.gatesStatus === 'loading') {
      return 'loading'
    }
    if (this.gatesStatus === 'failed') {
      return 'off'
    }
    return this.client.getFeatureGate(name).value ? 'on' : 'off'
  }

  getLayer(name: LayerName): Layer {
    if (!this.isActive) {
      return emptyLayer
    }
    return this.client.getLayer(name)
  }

  getDynamicConfig(name: DynamicConfigName): DynamicConfig {
    if (!this.isActive) {
      return emptyDynamicConfig
    }
    return this.client.getDynamicConfig(name)
  }

  logEvent(eventName: string, value: string, metadata?: Record<string, any>): void {
    if (!this.isActive) {
      return
    }
    // eslint-disable-next-line
    console.log(eventName, value, metadata)
    this.client.logEvent(eventName, value, metadata)
  }

  async identify(patch: Partial<Pick<StatsigUser, 'customIDs' | 'custom'>>): Promise<void> {
    if (!this.isActive) {
      return
    }
    const { user } = this.client.getContext()
    await this.client.updateUserAsync({
      ...user,
      customIDs: { ...user.customIDs, ...patch.customIDs },
      custom: { ...user.custom, ...patch.custom },
    })
  }

  getExperiment = (name: ExperimentName, options?: { disableExposureLog: boolean }): Experiment => {
    if (!this.isActive) {
      return emptyExperiment
    }
    return this.client.getExperiment(name, options)
  }

  allActiveExperiments(
    names: readonly ExperimentName[]
  ): Record<string, { name: string; value: Record<string, unknown> }> {
    if (!this.isActive) {
      return {}
    }

    return names.reduce((acc, name) => {
      const experiment = this.getExperiment(name, { disableExposureLog: false })

      // true, when user is in experiment, it is group name of the experiment
      if (experiment.groupName) {
        acc[name] = {
          name: experiment.name,
          // DWH uses group name as join as reference
          value: { ...experiment.value, group: experiment.groupName },
        }
      }

      return acc
    }, {})
  }

  onValuesUpdated(callback: (e: any) => void) {
    if (this.isActive && this.client) {
      this.client.on('values_updated', callback)
    }
  }
}
